import { useMemo } from 'react'
import styled, { type CSSProperties } from 'styled-components/macro'

import { Select, type SelectProps } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Field } from '@loadsmart/miranda-react'

import { mapToOptions, type Option } from '../../../domain/Option'
import { TRUCK_TYPES, type TruckType } from '../../../domain/Truck'
import { getTruckLabel } from '../../../presenters/truckPresenter'
import { toArray } from '../../../utils/array'

const StyledField = styled(Field)`
  min-width: unset;
`

type BaseProps = Omit<SelectProps, 'value' | 'onChange' | 'multiple'> & {
  label: string
  style?: CSSProperties
  truckTypes?: Array<TruckType>
  value?: Array<TruckType>
}

type SingleProps = BaseProps & {
  multiple: false
  value?: TruckType
  onChange?: (value?: TruckType) => void
}

type MultipleProps = BaseProps & {
  multiple: true
  value?: Array<TruckType>
  onChange?: (value?: Array<TruckType>) => void
}

export type TruckSelectProps = SingleProps | MultipleProps

export function TruckSelect({
  label,
  style,
  truckTypes,
  value,
  id = 'equipment',
  multiple = false,
  onChange,
  ...props
}: TruckSelectProps) {
  const selected = useMemo(() => {
    const options = mapToOptions({
      values: [...(value ?? [])],
      getValue: value => value,
      getLabel: value => getTruckLabel(value) ?? '',
    })

    if (options.length === 0) {
      return undefined
    } else if (multiple) {
      return options
    } else {
      return options[0]
    }
  }, [multiple, value])

  const options = useMemo(
    () =>
      mapToOptions({
        values: [...(truckTypes ?? TRUCK_TYPES)],
        getValue: value => value,
        getLabel: value => getTruckLabel(value) ?? '',
      }),
    [truckTypes]
  )

  function handleOnChange(event: EventLike<Selectable | Selectable[] | null>) {
    const newOptions = event?.target?.value as Array<Option> | Option | null

    const callback = onChange as (value?: Array<TruckType> | TruckType) => void
    const optionValues = toArray(newOptions)

    if (optionValues.length === 0) {
      callback(undefined)
    } else if (multiple) {
      callback(optionValues.map(({ value }) => value as TruckType))
    } else {
      callback(optionValues[0].value as TruckType)
    }
  }

  return (
    <StyledField style={style}>
      {label && <Field.Label id={id}>{label}</Field.Label>}
      <Select
        id={id}
        options={options}
        value={selected}
        multiple={multiple}
        onChange={handleOnChange}
        {...props}
      />
    </StyledField>
  )
}
